import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { selectOktaConfig } from '@optools/store/configApiSlice';
import { useAppDispatch, useAppSelector } from '@optools/store/hooks';
import { ReactNode, useEffect, useMemo } from 'react';

export interface AuthProps {
  children: ReactNode;
  onRestoreOriginalUri: (uri: string) => void;
}

export function Auth({ children, onRestoreOriginalUri }: AuthProps) {
  const dispatch = useAppDispatch();
  const oktaConfig = useAppSelector(selectOktaConfig);
  const oktaAuth = useMemo(() => new OktaAuth(oktaConfig), [oktaConfig]);

  const restoreOriginalUri = (_: OktaAuth, originalUri: string) => {
    onRestoreOriginalUri(toRelativeUrl(originalUri || '/', window.location.origin));
  };

  useEffect(() => {
    oktaAuth.start();

    // Workaround for Okta Auth JS not renewing tokens automatically on localhost
    if (process.env.NODE_ENV === 'development') {
      oktaAuth.tokenManager.on('expired', () => oktaAuth.authStateManager.updateAuthState());
      oktaAuth.tokenManager.on('renewed', () => oktaAuth.authStateManager.updateAuthState());
    }

    return () => {
      if (process.env.NODE_ENV === 'development') {
        oktaAuth.tokenManager.off('expired');
        oktaAuth.tokenManager.off('renewed');
      }
      oktaAuth.stop();
    };
  }, [dispatch, oktaAuth]);

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      {children}
    </Security>
  );
}
